import { useQuery } from '@apollo/client';
import { opportunitiesListApi } from '../api';
import { Account } from '@trnsact/trnsact-shared-types';
import { useSelector } from 'react-redux';
import { getOpportunitiesTableDealerColumns, getOpportunitiesTablePartnerColumns } from '../lib';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { useEffect, useMemo, useState } from 'react';
import { SelectOption } from 'global';
import { keyBy } from 'utils/arrayUtils';
import { OpportunitiesTableColumn } from '../types';

export function useOpportunitiesListColumns() {
  const account = useSelector((state: any) => state.account) as Account;
  const vp = useSelector((state: any) => state.vp);

  const { data: bpfTemplatesResponse, loading: isBpfTemplatesLoading } = useQuery(
    opportunitiesListApi.getBusinessProcessFlowTemplates,
    {
      skip: !account?.id,
      variables: { input: { accountId: account?.id } },
    }
  );

  const isTiersResultsHidden = useSelector(portalConfigurationsSelectors.isTiersResultsHidden);
  const allowManualVoCreation = useSelector(portalConfigurationsSelectors.allowManualVoCreation);

  const initialColumns = useMemo(() => {
    const bpfTemplates = bpfTemplatesResponse?.bpf?.templates ?? [];

    return account.accountType === 'Partner'
      ? getOpportunitiesTablePartnerColumns({ bpfTemplates })
      : getOpportunitiesTableDealerColumns({
          bpfTemplates,
          isTiersResultsHidden,
          allowManualVoCreation,
          isMultiLocationManagementEnabled: vp.multiLocationManagement,
        });
  }, [
    account.accountType,
    bpfTemplatesResponse?.bpf?.templates,
    isTiersResultsHidden,
    allowManualVoCreation,
    vp.multiLocationManagement,
  ]);

  const [allColumns, setAllColumns] = useState<OpportunitiesTableColumn[]>(initialColumns);

  const handleColumnsVisibilityChange = (options: SelectOption[]) => {
    const visibleColumnsMap = keyBy(options, 'value');
    setAllColumns(prev => prev.map(column => ({ ...column, isHidden: !visibleColumnsMap[column.renderKey] })));
  };

  useEffect(() => {
    setAllColumns(initialColumns);
  }, [initialColumns]);

  const initialVisibleColumns = useMemo(() => initialColumns.filter(({ isHidden }) => !isHidden), [initialColumns]);

  return {
    isLoading: isBpfTemplatesLoading,
    allColumns,
    initialVisibleColumns,
    visibleColumns: allColumns.filter(({ isHidden }) => !isHidden),
    handleColumnsVisibilityChange,
  };
}
